package com.tixforgigs.common.ui.components.structure


import com.tixforgigs.apax.models.AgeRestriction
import com.tixforgigs.tarami.AraiMessage.textSupervisorNotRequired
import com.tixforgigs.tarami.AraiMessage.textSupervisorRequired
import com.tixforgigs.tarami.GlobalMessage.messageMinimumAge
import io.kvision.core.Container
import io.kvision.html.link
import io.kvision.html.small
import io.kvision.html.span
import io.kvision.material.progress.circularProgress
import io.kvision.panel.SimplePanel
import io.kvision.panel.simplePanel


/**
 * ## Meta Panel
 *
 * A panel for displaying metadata entries in a structured format.
 */
fun Container.metaPanel(
  init: (SimplePanel.() -> Unit)? = null
): SimplePanel = simplePanel(
  className = "xm-meta",
  init = init
)


fun Container.metaMediumPanel(
  init: (SimplePanel.() -> Unit)? = null
): SimplePanel = simplePanel(
  className = "xm-meta xm-gap-medium",
  init = init
)


/**
 * ## Meta-Entry Panel
 *
 * A panel for displaying a single metadata entry with a label and content.
 */
fun Container.metaEntryPanel(
  label: String,
  content: String
): SimplePanel = simplePanel(
  className = "xm-meta-entry"
) {
  simplePanel(className = "xm-meta-label") {
    span(label)
  }
  simplePanel(className = "xm-meta-content") {
    span(content)
  }
}


/**
 * ## Age Restriction Panel
 *
 * A panel for displaying age restriction information with a label and content.
 */
fun Container.ageRestrictionPanel(
  ageRestriction: AgeRestriction?
): SimplePanel = simplePanel(
  className = "xm-small"
) {
  ageRestriction?.let { ageRestriction ->
    if (ageRestriction.isVisible) {
      smallPanel("${messageMinimumAge(ageRestriction.minimumAge)} " +
          "${if (ageRestriction.permissionRequired) textSupervisorRequired else textSupervisorNotRequired}")
    }
  }
}


/**
 * ## Container Panel
 *
 * A panel for grouping related content in a structured format.
 */
fun Container.containerPanel(
  init: (SimplePanel.() -> Unit)? = null
): SimplePanel = simplePanel(
  className = "xm-container",
  init = init
)


fun Container.eventsPanel(
  init: (SimplePanel.() -> Unit)? = null
): SimplePanel = simplePanel(
  className = "xm-container xm-col xm-gap",
  init = init
)


/**
 * ## Hero Panel
 *
 * A panel for displaying a hero section with a title and optional content.
 */
fun Container.heroPanel(
  init: (SimplePanel.() -> Unit)? = null
): SimplePanel = simplePanel(
  className = "xm-hero",
  init = init
)


/**
 * ## Small Panel
 *
 * A panel for displaying small content in a compact format.
 */
fun Container.smallPanel(
  content: String
): SimplePanel = simplePanel(
  className = "xm-small"
) {
  small(content.uppercase())
}


/**
 * ## Loading Panel
 *
 * A panel for displaying a loading indicator with optional content.
 */
fun Container.loadingPanel(
  indeterminate : Boolean = true,
  content: String? = null
): SimplePanel = simplePanel(
  className = "xm-col xm-center xm-loading"
) {
  circularProgress(indeterminate = indeterminate)
  content?.let {
    simplePanel(className = "xm-col") {
      span(content)
    }
  }
}


/**
 * ## Footer Link Panel
 *
 * A panel for displaying a link with a label, URL, and optional description.
 */
fun Container.footerLinkPanel(
  label: String,
  url: String,
  desc: String? = null
): SimplePanel = simplePanel(
  className = "xm-footer-links"
) {
  link(
    label = label,
    target = "_blank",
    url = url
  ) {
    title = desc ?: label
  }
}
